import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [primaryCurrency, setPrimaryCurrency] = useState('USD');
  const [secondaryCurrency, setSecondaryCurrency] = useState(null);
  const [userCurrencies, setUserCurrencies] = useState([]); // All user's preferred currencies
  const [activeCurrency, setActiveCurrency] = useState('USD'); // Currently viewing currency
  const [exchangeRates, setExchangeRates] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Get auth headers
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Load exchange rates for active currency
  const loadExchangeRates = async (baseCurrency) => {
    try {
      const response = await axios.get(`${API}/api/exchange-rates/${baseCurrency}`);
      setExchangeRates(response.data.rates || {});
      console.log('Exchange rates loaded for', baseCurrency);
    } catch (error) {
      console.error('Error loading exchange rates:', error);
    }
  };

  // Load user currency preferences
  useEffect(() => {
    const loadUserCurrencies = async () => {
      try {
        const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
        
        if (token) {
          // User is logged in - get from profile
          const response = await axios.get(`${API}/api/user/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Check for currencies array first (new multi-currency format)
          const currencies = response.data.currencies || [];
          if (currencies.length > 0) {
            const primaryCurr = currencies.find(c => c.is_primary);
            const primary = primaryCurr?.code || currencies[0]?.code || 'USD';
            const secondary = currencies.find(c => !c.is_primary)?.code || null;
            
            setPrimaryCurrency(primary);
            setSecondaryCurrency(secondary);
            setUserCurrencies(currencies);
            setActiveCurrency(primary);
          } else {
            // Fallback to old format
            const primary = response.data.currency || 'USD';
            const secondary = response.data.secondary_currency || null;
            
            setPrimaryCurrency(primary);
            setSecondaryCurrency(secondary);
            setUserCurrencies([{ code: primary, is_primary: true }]);
            if (secondary) {
              setUserCurrencies(prev => [...prev, { code: secondary, is_primary: false }]);
            }
            setActiveCurrency(primary);
          }
          
          // Load exchange rates for primary currency
          await loadExchangeRates(activeCurrency);
        } else {
          // User not logged in - get from IP detection
          try {
            const ipResponse = await axios.get(`${API}/api/detect-language`);
            const primary = ipResponse.data.currency || 'USD';
            
            setPrimaryCurrency(primary);
            setUserCurrencies([{ code: primary, is_primary: true }]);
            setActiveCurrency(primary);
            await loadExchangeRates(primary);
          } catch {
            // Fallback to USD
            setPrimaryCurrency('USD');
            setUserCurrencies([{ code: 'USD', is_primary: true }]);
            setActiveCurrency('USD');
          }
        }
      } catch (error) {
        console.error('Error loading user currencies:', error);
        // Default to USD if error
        setPrimaryCurrency('USD');
        setSecondaryCurrency(null);
        setUserCurrencies([{ code: 'USD', is_primary: true }]);
        setActiveCurrency('USD');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserCurrencies();
  }, [API]);

  // Convert amount from one currency to another
  const convertAmount = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    // If we don't have rates, return original amount
    if (!exchangeRates || Object.keys(exchangeRates).length === 0) {
      return amount;
    }

    // Convert through the active currency base
    // First convert from source to active, then active to target
    if (fromCurrency === activeCurrency) {
      const rate = exchangeRates[toCurrency];
      return rate ? amount * rate : amount;
    } else if (toCurrency === activeCurrency) {
      const rate = exchangeRates[fromCurrency];
      return rate ? amount / rate : amount;
    } else {
      // Convert through active currency
      const fromRate = exchangeRates[fromCurrency];
      const toRate = exchangeRates[toCurrency];
      if (fromRate && toRate) {
        return (amount / fromRate) * toRate;
      }
      return amount;
    }
  };

  // Format amount in active currency
  const formatAmount = (amount, sourceCurrency = null, options = {}) => {
    // If source currency is provided and different from active, convert it
    let displayAmount = amount;
    if (sourceCurrency && sourceCurrency !== activeCurrency) {
      displayAmount = convertAmount(amount, sourceCurrency, activeCurrency);
    }
    
    return formatCurrency(displayAmount, activeCurrency, options);
  };

  // Get currency symbol for active currency
  const getCurrencySymbolForUser = () => {
    return getCurrencySymbol(activeCurrency);
  };

  // Switch active viewing currency
  const switchCurrency = async (currency) => {
    setActiveCurrency(currency);
    // Reload exchange rates for new currency
    await loadExchangeRates(currency);
  };

  // Update user currencies (when saved in profile)
  const updateUserCurrencies = async (primary, secondary) => {
    setPrimaryCurrency(primary);
    setSecondaryCurrency(secondary);
    // If active currency is not one of the new currencies, switch to primary
    if (activeCurrency !== primary && activeCurrency !== secondary) {
      await switchCurrency(primary);
    }
  };

  // Get available currencies for user (from profile)
  const getAvailableCurrencies = () => {
    if (userCurrencies.length > 0) {
      return userCurrencies.map(c => c.code);
    }
    // Fallback to primary + secondary
    const currencies = [primaryCurrency];
    if (secondaryCurrency) {
      currencies.push(secondaryCurrency);
    }
    return currencies;
  };

  // Set user currency (called from profile settings)
  const setUserCurrency = (currency) => {
    setPrimaryCurrency(currency);
    setActiveCurrency(currency);
  };

  const value = {
    primaryCurrency,
    secondaryCurrency,
    userCurrencies,
    activeCurrency,
    exchangeRates,
    isLoading,
    setPrimaryCurrency,
    setSecondaryCurrency,
    setActiveCurrency: switchCurrency,
    setUserCurrency,
    convertAmount,
    formatAmount,
    getCurrencySymbol: getCurrencySymbolForUser,
    updateUserCurrencies,
    getAvailableCurrencies
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;